// Quick-add dialog for project related records, scoped to the active phase.
// Handles: action | visit | inspection | document (upload / template / existing / required).
import { useEffect, useState } from "react";
import { X, Loader2, Save, Upload, FileStack, Copy, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAssignRecipients, AssignRecipientsStep } from "@/components/AssignRecipientsStep";

const VISIT_TYPES = ["Fire Risk Assessment", "Fire door inspection", "H&S audit", "Compliance check",
    "BSC evidence walk", "Legionella / water hygiene", "Emergency lighting", "Asbestos re-inspection",
    "General inspection", "Other"];
const DOC_CATEGORIES = ["Reports", "RAMS", "CPPs", "PCI / PCIP packs", "Certificates",
    "Fire risk assessments", "Fire strategies", "Site visit reports", "Building Safety Case documents",
    "Golden Thread documents", "O&M manuals", "Existing fire documentation"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];

const TITLES = { action: "Add action", visit: "Add site visit", inspection: "Add inspection / audit", document: "Add document" };
const fi = "w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none";
const fiStyle = { borderColor: "#E5E0DA" };
const Lbl = ({ children }) => <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>{children}</span>;

export default function AddRecordDialog({ type, project, phase, suggestions = [], presetTemplateCode = "", onClose, onCreated }) {
    const phasePayload = { phase_key: phase?.key || null, phase_label: phase?.label || null };
    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid={`add-record-dialog-${type}`} className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[520px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 pt-4 pb-3 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <div>
                        <h3 className="font-display text-[16px] font-bold text-[#1F1F1F]">{TITLES[type]}</h3>
                        {phase?.label && <p className="text-[11px]" style={{ color: "#9A968F" }}>Phase: {phase.label}</p>}
                    </div>
                    <button data-testid="add-record-close" onClick={onClose} className="w-8 h-8 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-5 py-4 overflow-y-auto">
                    {type === "action" && <ActionForm project={project} phasePayload={phasePayload} suggestions={suggestions} onCreated={onCreated} />}
                    {type === "visit" && <VisitForm project={project} phasePayload={phasePayload} onCreated={onCreated} />}
                    {type === "inspection" && <InspectionForm project={project} phasePayload={phasePayload} suggestions={suggestions} presetTemplateCode={presetTemplateCode} onCreated={onCreated} />}
                    {type === "document" && <DocumentForm project={project} phase={phase} phasePayload={phasePayload} onCreated={onCreated} />}
                </div>
            </div>
        </div>
    );
}

function Footer({ busy, onSubmit, label = "Add" }) {
    return (
        <div className="flex items-center justify-end gap-2.5 pt-4 mt-2 border-t" style={{ borderColor: "#F0EDE8" }}>
            <button data-testid="add-record-submit" onClick={onSubmit} disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{label}
            </button>
        </div>
    );
}

function ActionForm({ project, phasePayload, suggestions, onCreated }) {
    const [f, setF] = useState({ title: "", assignee_name: "", assignee_email: "", assignee_type: "internal", due_date: "", priority: "Medium" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    async function submit() {
        if (!f.title.trim()) { toast.error("Action title is required"); return; }
        setBusy(true);
        try {
            await api.post("/actions", {
                client_id: project.client_id, project_id: project.id, ...phasePayload,
                title: f.title.trim(), assignee_name: f.assignee_name.trim() || null,
                assignee_email: f.assignee_email.trim() || null,
                due_date: f.due_date || null, priority: f.priority, source: "Project",
            });
            onCreated();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
    }
    return (
        <div className="space-y-3">
            <label className="block"><Lbl>Title</Lbl><input data-testid="add-action-title" value={f.title} onChange={(e) => set("title", e.target.value)} className={fi} style={fiStyle} placeholder="What needs doing?" /></label>
            <div className="flex gap-2">
                {["internal", "external"].map((t) => (
                    <button key={t} data-testid={`add-action-type-${t}`} onClick={() => set("assignee_type", t)} className={`flex-1 px-3 py-2 rounded-[8px] text-[12px] font-semibold border capitalize ${f.assignee_type === t ? "text-white border-transparent" : "bg-white text-[#2D2D2D]"}`} style={f.assignee_type === t ? { background: "var(--brand-accent,#C75A1B)" } : fiStyle}>{t}</button>
                ))}
            </div>
            <label className="block"><Lbl>Assign to</Lbl>
                <input data-testid="add-action-assignee" list="add-rec-suggest" value={f.assignee_name} onChange={(e) => set("assignee_name", e.target.value)} className={fi} style={fiStyle} placeholder={f.assignee_type === "internal" ? "Priority Risk team member" : "Client / contractor contact"} />
                <datalist id="add-rec-suggest">{Array.from(new Set(suggestions)).map((s) => <option key={s} value={s} />)}</datalist>
            </label>
            {f.assignee_type === "external" && <label className="block"><Lbl>Email (optional)</Lbl><input data-testid="add-action-email" type="email" value={f.assignee_email} onChange={(e) => set("assignee_email", e.target.value)} className={fi} style={fiStyle} placeholder="name@company.co.uk" /></label>}
            <div className="grid grid-cols-2 gap-3">
                <label className="block"><Lbl>Due date</Lbl><input data-testid="add-action-due" type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className={fi} style={fiStyle} /></label>
                <label className="block"><Lbl>Priority</Lbl><select data-testid="add-action-priority" value={f.priority} onChange={(e) => set("priority", e.target.value)} className={fi} style={fiStyle}>{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></label>
            </div>
            <Footer busy={busy} onSubmit={submit} label="Add action" />
        </div>
    );
}

function VisitForm({ project, phasePayload, onCreated }) {
    const [f, setF] = useState({ title: "", visit_type: "Compliance check", assignee_id: "", scheduled_date: "", scheduled_time: "" });
    const [internalUsers, setInternalUsers] = useState([]);
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    useEffect(() => { api.get("/visits/assignable-users").then((r) => setInternalUsers(r.data)).catch(() => setInternalUsers([])); }, []);
    async function submit() {
        if (!f.scheduled_date) { toast.error("Pick a date"); return; }
        setBusy(true);
        try {
            await api.post("/visits", {
                client_id: project.client_id, project_id: project.id, ...phasePayload,
                title: f.title.trim() || null, visit_type: f.visit_type,
                assignee_id: f.assignee_id || null,
                scheduled_date: f.scheduled_date, scheduled_time: f.scheduled_time || null,
            });
            onCreated();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
    }
    return (
        <div className="space-y-3">
            <label className="block"><Lbl>Title (optional)</Lbl><input data-testid="add-visit-title" value={f.title} onChange={(e) => set("title", e.target.value)} className={fi} style={fiStyle} placeholder="e.g. Pre-occupation evidence walk" /></label>
            <label className="block"><Lbl>Visit type</Lbl><select data-testid="add-visit-type" value={f.visit_type} onChange={(e) => set("visit_type", e.target.value)} className={fi} style={fiStyle}>{VISIT_TYPES.map((v) => <option key={v}>{v}</option>)}</select></label>
            <label className="block"><Lbl>Assignee</Lbl>
                <select data-testid="add-visit-assignee" value={f.assignee_id} onChange={(e) => set("assignee_id", e.target.value)} className={fi} style={fiStyle}>
                    <option value="">— Unassigned —</option>
                    {internalUsers.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
                <label className="block"><Lbl>Date</Lbl><input data-testid="add-visit-date" type="date" value={f.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} className={fi} style={fiStyle} /></label>
                <label className="block"><Lbl>Time (optional)</Lbl><input data-testid="add-visit-time" type="time" value={f.scheduled_time} onChange={(e) => set("scheduled_time", e.target.value)} className={fi} style={fiStyle} /></label>
            </div>
            <Footer busy={busy} onSubmit={submit} label="Schedule visit" />
        </div>
    );
}

function InspectionForm({ project, phasePayload, suggestions, presetTemplateCode = "", onCreated }) {
    const [templates, setTemplates] = useState([]);
    const [sites, setSites] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [f, setF] = useState({ template_id: "", site_id: "", building_id: "", title: "", assignee_name: "", due_date: "" });
    const [busy, setBusy] = useState(false);
    const [creatingSite, setCreatingSite] = useState(false);
    const [newSite, setNewSite] = useState({ name: "", address: "" });
    const [savingSite, setSavingSite] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    const assign = useAssignRecipients(project.client_id);

    const projectSiteIds = project.site_ids || (project.site_id ? [project.site_id] : []);

    const loadSites = async () => {
        try {
            const r = await api.get("/sites", { params: { client_id: project.client_id } });
            setSites(r.data.filter((s) => projectSiteIds.includes(s.id)));
        } catch { setSites([]); }
    };
    useEffect(() => {
        api.get("/forms/templates").then((r) => {
            setTemplates(r.data);
            if (presetTemplateCode) {
                const match = r.data.find((t) => t.code === presetTemplateCode);
                if (match) set("template_id", match.id);
            }
        }).catch(() => setTemplates([]));
        api.get("/buildings", { params: { client_id: project.client_id } }).then((r) => setBuildings(r.data)).catch(() => setBuildings([]));
        loadSites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const siteBuildings = buildings.filter((b) => b.site_id === f.site_id);

    async function createSite() {
        if (!newSite.name.trim()) { toast.error("Site name is required"); return; }
        setSavingSite(true);
        try {
            const { data: site } = await api.post("/sites", {
                client_id: project.client_id, name: newSite.name.trim(),
                address: newSite.address.trim() || undefined,
            });
            // allocate the new site to this project
            await api.patch(`/projects/${project.id}`, { site_ids: [...projectSiteIds, site.id] });
            projectSiteIds.push(site.id);
            setSites((prev) => [...prev, site]);
            set("site_id", site.id);
            setCreatingSite(false);
            setNewSite({ name: "", address: "" });
            toast.success(`Site created & linked: ${site.name}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create site");
        } finally { setSavingSite(false); }
    }

    async function submit() {
        if (!f.template_id) { toast.error("Choose an inspection template"); return; }
        if (!f.site_id) { toast.error("Select a site for this inspection"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/forms/instances", {
                template_id: f.template_id, client_id: project.client_id, project_id: project.id,
                site_id: f.site_id, building_id: f.building_id || null, ...phasePayload,
                title: f.title.trim() || null, assignee_name: f.assignee_name.trim() || null, due_date: f.due_date || null,
                assigned_recipients: assign.recipients.length ? assign.recipients : undefined,
                assigned_message: assign.message.trim() || undefined,
            });
            onCreated(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
    }
    return (
        <div className="space-y-3">
            <label className="block"><Lbl>Inspection template</Lbl><select data-testid="add-inspection-template" value={f.template_id} onChange={(e) => set("template_id", e.target.value)} className={fi} style={fiStyle}>
                <option value="">Select a template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select></label>

            {/* Site (mandatory) → Building (optional, within the site) */}
            {creatingSite ? (
                <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }} data-testid="add-inspection-newsite">
                    <Lbl>New site for this project</Lbl>
                    <input data-testid="add-inspection-newsite-name" value={newSite.name} onChange={(e) => setNewSite((p) => ({ ...p, name: e.target.value }))} className={fi} style={fiStyle} placeholder="Site name" />
                    <input data-testid="add-inspection-newsite-address" value={newSite.address} onChange={(e) => setNewSite((p) => ({ ...p, address: e.target.value }))} className={fi} style={fiStyle} placeholder="Address (optional)" />
                    <div className="flex gap-2">
                        <button data-testid="add-inspection-newsite-save" onClick={createSite} disabled={savingSite}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            {savingSite ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Create & link
                        </button>
                        <button onClick={() => setCreatingSite(false)} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <Lbl>Site <span style={{ color: "#B33A2E" }}>*</span></Lbl>
                        <select data-testid="add-inspection-site" value={f.site_id} onChange={(e) => { set("site_id", e.target.value); set("building_id", ""); }} className={fi} style={fiStyle}>
                            <option value="">Select a site…</option>
                            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <button type="button" data-testid="add-inspection-add-site" onClick={() => setCreatingSite(true)}
                            className="mt-1 text-[11px] font-semibold" style={{ color: "var(--brand-accent,#C75A1B)" }}>+ New site</button>
                    </label>
                    <label className="block">
                        <Lbl>Building / address (optional)</Lbl>
                        <select data-testid="add-inspection-building" value={f.building_id} disabled={!f.site_id} onChange={(e) => set("building_id", e.target.value)} className={fi} style={fiStyle}>
                            <option value="">{f.site_id ? "— Whole site —" : "Select a site first"}</option>
                            {siteBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </label>
                </div>
            )}

            <label className="block"><Lbl>Title (optional)</Lbl><input data-testid="add-inspection-title" value={f.title} onChange={(e) => set("title", e.target.value)} className={fi} style={fiStyle} placeholder="Defaults to template name + date" /></label>
            <div className="grid grid-cols-2 gap-3">
                <label className="block"><Lbl>Assignee</Lbl><input data-testid="add-inspection-assignee" list="add-rec-suggest" value={f.assignee_name} onChange={(e) => set("assignee_name", e.target.value)} className={fi} style={fiStyle} /><datalist id="add-rec-suggest">{Array.from(new Set(suggestions)).map((s) => <option key={s} value={s} />)}</datalist></label>
                <label className="block"><Lbl>Due date</Lbl><input data-testid="add-inspection-due" type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className={fi} style={fiStyle} /></label>
            </div>
            <AssignRecipientsStep state={assign} testIdPrefix="add-inspection-assign" />
            <Footer busy={busy} onSubmit={submit} label="Create inspection" />
        </div>
    );
}

function DocumentForm({ project, phase, phasePayload, onCreated }) {
    const MODES = [
        { id: "upload", label: "Upload", icon: Upload },
        { id: "template", label: "Template", icon: FileStack },
        { id: "existing", label: "Existing", icon: Copy },
        { id: "required", label: "Required", icon: ListChecks },
    ];
    const [mode, setMode] = useState("upload");
    const [busy, setBusy] = useState(false);
    // shared
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("Reports");
    // upload
    const [file, setFile] = useState(null);
    // template / existing / required selections
    const [templates, setTemplates] = useState([]);
    const [existing, setExisting] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [sourceId, setSourceId] = useState("");
    const [requiredName, setRequiredName] = useState("");
    const requiredDocs = phase?.required_documents || [];

    useEffect(() => {
        if (mode === "template") api.get("/document-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
        if (mode === "existing") api.get("/documents", { params: { client_id: project.client_id } }).then((r) => setExisting(r.data)).catch(() => setExisting([]));
    }, [mode, project.client_id]);

    async function submit() {
        setBusy(true);
        try {
            if (mode === "upload") {
                if (!file) { toast.error("Choose a file"); setBusy(false); return; }
                if (!title.trim()) { toast.error("Title is required"); setBusy(false); return; }
                const fd = new FormData();
                fd.append("file", file);
                fd.append("client_id", project.client_id);
                fd.append("project_id", project.id);
                fd.append("title", title.trim());
                fd.append("category", category);
                if (phasePayload.phase_key) fd.append("phase_key", phasePayload.phase_key);
                if (phasePayload.phase_label) fd.append("phase_label", phasePayload.phase_label);
                await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            } else if (mode === "template") {
                if (!templateId) { toast.error("Choose a template"); setBusy(false); return; }
                await api.post("/documents/from-source", { mode: "template", project_id: project.id, ...phasePayload, template_id: templateId, title: title.trim() || null });
            } else if (mode === "existing") {
                if (!sourceId) { toast.error("Choose a document to copy"); setBusy(false); return; }
                await api.post("/documents/from-source", { mode: "existing", project_id: project.id, ...phasePayload, source_document_id: sourceId, title: title.trim() || null });
            } else if (mode === "required") {
                if (!requiredName) { toast.error("Choose a required document"); setBusy(false); return; }
                await api.post("/documents/from-source", { mode: "required", project_id: project.id, ...phasePayload, required_name: requiredName });
            }
            onCreated();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
    }

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-4 gap-1.5">
                {MODES.map((m) => (
                    <button key={m.id} data-testid={`add-doc-mode-${m.id}`} onClick={() => setMode(m.id)}
                        className={`flex flex-col items-center gap-1 px-1 py-2 rounded-[8px] text-[10.5px] font-semibold border ${mode === m.id ? "text-white border-transparent" : "bg-white text-[#2D2D2D]"}`}
                        style={mode === m.id ? { background: "var(--brand-accent,#C75A1B)" } : fiStyle}>
                        <m.icon className="w-4 h-4" />{m.label}
                    </button>
                ))}
            </div>

            {mode === "upload" && (
                <>
                    <label className="block"><Lbl>File</Lbl><input data-testid="add-doc-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className={fi} style={fiStyle} /></label>
                    <label className="block"><Lbl>Title</Lbl><input data-testid="add-doc-title" value={title} onChange={(e) => setTitle(e.target.value)} className={fi} style={fiStyle} placeholder="Document title" /></label>
                    <label className="block"><Lbl>Category</Lbl><select data-testid="add-doc-category" value={category} onChange={(e) => setCategory(e.target.value)} className={fi} style={fiStyle}>{DOC_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></label>
                </>
            )}
            {mode === "template" && (
                <>
                    <p className="text-[11px]" style={{ color: "#6F6A64" }}>Copy a master template file into this phase.</p>
                    <label className="block"><Lbl>Template</Lbl><select data-testid="add-doc-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} className={fi} style={fiStyle}>
                        <option value="">Select a template…</option>
                        {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.category}</option>)}
                    </select></label>
                    <label className="block"><Lbl>Title (optional)</Lbl><input data-testid="add-doc-title" value={title} onChange={(e) => setTitle(e.target.value)} className={fi} style={fiStyle} placeholder="Defaults to template name" /></label>
                </>
            )}
            {mode === "existing" && (
                <>
                    <p className="text-[11px]" style={{ color: "#6F6A64" }}>Reuse a document already in this client's library.</p>
                    <label className="block"><Lbl>Document</Lbl><select data-testid="add-doc-existing" value={sourceId} onChange={(e) => setSourceId(e.target.value)} className={fi} style={fiStyle}>
                        <option value="">Select a document…</option>
                        {existing.map((d) => <option key={d.id} value={d.id}>{d.title} · {d.category}</option>)}
                    </select></label>
                    <label className="block"><Lbl>Title (optional)</Lbl><input data-testid="add-doc-title" value={title} onChange={(e) => setTitle(e.target.value)} className={fi} style={fiStyle} placeholder="Defaults to 'Copy of …'" /></label>
                </>
            )}
            {mode === "required" && (
                <>
                    <p className="text-[11px]" style={{ color: "#6F6A64" }}>Create a placeholder slot for a document this phase requires (attach the file later).</p>
                    {requiredDocs.length === 0 ? (
                        <div className="text-[12px] italic text-[#9A968F] py-2">This phase has no required documents defined on its template.</div>
                    ) : (
                        <label className="block"><Lbl>Required document</Lbl><select data-testid="add-doc-required" value={requiredName} onChange={(e) => setRequiredName(e.target.value)} className={fi} style={fiStyle}>
                            <option value="">Select…</option>
                            {requiredDocs.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select></label>
                    )}
                </>
            )}
            <Footer busy={busy} onSubmit={submit} label="Add document" />
        </div>
    );
}
